import { BaseEffect } from '../BaseEffect.js';

/**
 * Neon bloom effect implementation
 * Creates a vibrant, animated neon effect with multi-pass bloom and edge detection
 */
export class NeonBloomEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            primaryColorR: 1.0,      // Primary neon color R
            primaryColorG: 0.2,      // Primary neon color G
            primaryColorB: 0.8,      // Primary neon color B
            secondaryColorR: 0.2,    // Secondary neon color R
            secondaryColorG: 1.0,    // Secondary neon color G
            secondaryColorB: 0.4,    // Secondary neon color B
            glowIntensity: 1.5,      // Overall glow intensity
            edgeIntensity: 0.8,      // Edge glow intensity
            chromaticAberration: 0.3, // Color separation amount
            scanlineIntensity: 0.15,  // Intensity of retro scanlines
            scanlineSpeed: 2.0,       // Speed of scanline movement
            pulseSpeed: 1.0,         // Speed of the glow pulse
            waveSpeed: 0.5,          // Speed of the wave pattern
            distortionAmount: 0.2,    // Amount of wave distortion
            animate: true            // Whether to animate the effect
        };

        super('neon', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform vec3 u_primaryColor;
            uniform vec3 u_secondaryColor;
            uniform float u_glowIntensity;
            uniform float u_edgeIntensity;
            uniform float u_chromaticAberration;
            uniform float u_scanlineIntensity;
            uniform float u_scanlineSpeed;
            uniform float u_pulseSpeed;
            uniform float u_waveSpeed;
            uniform float u_distortionAmount;
            
            varying vec2 v_texcoord;

            // Edge detection matrix
            const mat3 sobelX = mat3(-1.0, 0.0, 1.0,
                                   -2.0, 0.0, 2.0,
                                   -1.0, 0.0, 1.0);
            
            const mat3 sobelY = mat3(-1.0, -2.0, -1.0,
                                    0.0,  0.0,  0.0,
                                    1.0,  2.0,  1.0);

            // Helper function for edge detection
            float getEdgeValue(vec2 uv) {
                float x = 0.0;
                float y = 0.0;
                
                // Calculate pixel size
                vec2 pixelSize = 1.0 / u_resolution;
                
                // Apply Sobel operator
                for(int i = -1; i <= 1; i++) {
                    for(int j = -1; j <= 1; j++) {
                        vec2 offset = vec2(float(i), float(j)) * pixelSize;
                        float luminance = dot(texture2D(u_image, uv + offset).rgb, vec3(0.299, 0.587, 0.114));
                        x += luminance * sobelX[i+1][j+1];
                        y += luminance * sobelY[i+1][j+1];
                    }
                }
                
                return sqrt(x*x + y*y);
            }

            // Wave distortion
            vec2 getWaveOffset(vec2 uv) {
                float time = u_time * u_waveSpeed;
                vec2 offset = vec2(
                    sin(uv.y * 10.0 + time) * 0.01,
                    cos(uv.x * 10.0 + time) * 0.01
                ) * u_distortionAmount;
                return offset;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                vec2 waveOffset = getWaveOffset(uv);
                
                // Apply chromatic aberration
                float aberration = u_chromaticAberration * 0.01;
                vec2 redOffset = waveOffset + vec2(aberration, 0.0);
                vec2 blueOffset = waveOffset - vec2(aberration, 0.0);
                
                // Sample colors with chromatic aberration
                vec4 baseColorR = texture2D(u_image, uv + redOffset);
                vec4 baseColorG = texture2D(u_image, uv + waveOffset);
                vec4 baseColorB = texture2D(u_image, uv + blueOffset);
                
                // Combine for base color with chromatic aberration
                vec4 baseColor = vec4(baseColorR.r, baseColorG.g, baseColorB.b, baseColorG.a);
                
                // Calculate edge glow
                float edge = getEdgeValue(uv) * u_edgeIntensity;
                
                // Create animated pulse
                float pulse = 1.0 + sin(u_time * u_pulseSpeed) * 0.3;
                
                // Create animated scanlines
                float scanline = sin(uv.y * u_resolution.y * 0.5 - u_time * u_scanlineSpeed) * 0.5 + 0.5;
                scanline = mix(1.0, scanline, u_scanlineIntensity);
                
                // Create animated neon glow
                vec3 primaryGlow = u_primaryColor * (pulse * u_glowIntensity);
                vec3 secondaryGlow = u_secondaryColor * (1.0 - pulse) * u_glowIntensity;
                
                // Mix glows based on edge detection
                vec3 edgeGlow = mix(primaryGlow, secondaryGlow, sin(u_time * 2.0) * 0.5 + 0.5) * edge;
                
                // Combine everything
                vec3 finalColor = baseColor.rgb + edgeGlow;
                finalColor *= scanline;
                
                // Add subtle color bleeding
                finalColor += primaryGlow * 0.1 * (1.0 + sin(uv.x * 20.0 + u_time));
                finalColor += secondaryGlow * 0.1 * (1.0 + cos(uv.y * 20.0 + u_time));
                
                gl_FragColor = vec4(finalColor, baseColor.a);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_primaryColor', { type: '3fv' });
        this.addUniform('u_secondaryColor', { type: '3fv' });
        this.addUniform('u_glowIntensity', { type: '1f' });
        this.addUniform('u_edgeIntensity', { type: '1f' });
        this.addUniform('u_chromaticAberration', { type: '1f' });
        this.addUniform('u_scanlineIntensity', { type: '1f' });
        this.addUniform('u_scanlineSpeed', { type: '1f' });
        this.addUniform('u_pulseSpeed', { type: '1f' });
        this.addUniform('u_waveSpeed', { type: '1f' });
        this.addUniform('u_distortionAmount', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_primaryColor: { 
                value: [this.params.primaryColorR, this.params.primaryColorG, this.params.primaryColorB],
                type: '3fv'
            },
            u_secondaryColor: { 
                value: [this.params.secondaryColorR, this.params.secondaryColorG, this.params.secondaryColorB],
                type: '3fv'
            },
            u_glowIntensity: { value: this.params.glowIntensity, type: '1f' },
            u_edgeIntensity: { value: this.params.edgeIntensity, type: '1f' },
            u_chromaticAberration: { value: this.params.chromaticAberration, type: '1f' },
            u_scanlineIntensity: { value: this.params.scanlineIntensity, type: '1f' },
            u_scanlineSpeed: { value: this.params.scanlineSpeed, type: '1f' },
            u_pulseSpeed: { value: this.params.pulseSpeed, type: '1f' },
            u_waveSpeed: { value: this.params.waveSpeed, type: '1f' },
            u_distortionAmount: { value: this.params.distortionAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            primaryColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Primary Color R'
            },
            primaryColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Primary Color G'
            },
            primaryColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Primary Color B'
            },
            secondaryColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Secondary Color R'
            },
            secondaryColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Secondary Color G'
            },
            secondaryColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Secondary Color B'
            },
            glowIntensity: {
                min: 0,
                max: 3,
                step: 0.1,
                label: 'Glow Intensity'
            },
            edgeIntensity: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Edge Intensity'
            },
            chromaticAberration: {
                min: 0,
                max: 1,
                step: 0.05,
                label: 'Color Separation'
            },
            scanlineIntensity: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Scanline Intensity'
            },
            scanlineSpeed: {
                min: 0,
                max: 5,
                step: 0.1,
                label: 'Scanline Speed'
            },
            pulseSpeed: {
                min: 0,
                max: 3,
                step: 0.1,
                label: 'Pulse Speed'
            },
            waveSpeed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Wave Speed'
            },
            distortionAmount: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Wave Distortion'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 